import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Users, FileText, Calendar, Mail, Phone, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pagesAPI } from '../services/api';

const PrivacyPolicy = () => {
  const [pageData, setPageData] = useState({
    title: 'Privacy Policy',
    subtitle: 'Your privacy is important to us',
    lastUpdated: 'December 1, 2024',
    companyName: 'Niswa Digital Solutions',
    contactEmail: 'privacy@example.com',
    contactPhone: '+1 (555) 123-4567',
    sections: [
      {
        id: 'introduction',
        title: 'Introduction',
        content: 'This Privacy Policy describes how we collect, use, and protect your personal information when you use our services.',
        icon: Shield
      },
      {
        id: 'information-collection',
        title: 'Information We Collect',
        content: 'We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support.',
        subsections: [
          {
            title: 'Personal Information',
            content: 'Name, email address, phone number, and billing information.'
          },
          {
            title: 'Usage Information',
            content: 'How you interact with our website and services.'
          },
          {
            title: 'Technical Information',
            content: 'IP address, browser type, device information, and cookies.'
          }
        ],
        icon: Eye
      },
      {
        id: 'information-use',
        title: 'How We Use Your Information',
        content: 'We use the information we collect to provide, maintain, and improve our services.',
        subsections: [
          {
            title: 'Service Provision',
            content: 'To provide and maintain our services and process transactions.'
          },
          {
            title: 'Communication',
            content: 'To communicate with you about our services and updates.'
          },
          {
            title: 'Improvement',
            content: 'To improve our services and develop new features.'
          }
        ],
        icon: Lock
      },
      {
        id: 'information-sharing',
        title: 'Information Sharing',
        content: 'We do not sell, trade, or otherwise transfer your personal information to third parties without your consent.',
        icon: Users
      },
      {
        id: 'data-security',
        title: 'Data Security',
        content: 'We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',
        icon: Shield
      },
      {
        id: 'cookies',
        title: 'Cookies and Tracking',
        content: 'We use cookies and similar technologies to enhance your experience and collect usage information.',
        icon: Eye
      },
      {
        id: 'your-rights',
        title: 'Your Rights',
        content: 'You have the right to access, correct, or delete your personal information. Contact us to exercise these rights.',
        icon: Users
      },
      {
        id: 'children-privacy',
        title: 'Children\'s Privacy',
        content: 'Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13.',
        icon: Shield
      },
      {
        id: 'changes',
        title: 'Changes to This Policy',
        content: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.',
        icon: FileText
      },
      {
        id: 'contact',
        title: 'Contact Us',
        content: 'If you have any questions about this Privacy Policy, please contact us.',
        icon: Mail
      }
    ]
  });

  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    // Fetch page data from API
    const fetchPageData = async () => {
      try {
        const response = await pagesAPI.getByType('privacy-policy');
        if (response?.data?.success) {
          const apiData = response.data.data;
          
          // Map API data to component state, ensuring icons are preserved
          const mappedSections = (apiData.content?.sections || pageData.sections).map(section => {
            // Find the corresponding default section to get the icon
            const defaultSection = pageData.sections.find(s => s.id === section.id);
            return {
              ...section,
              icon: defaultSection?.icon || Shield // Fallback to Shield if no icon found
            };
          });
          
          setPageData({
            title: apiData.title || pageData.title,
            subtitle: apiData.subtitle || pageData.subtitle,
            lastUpdated: apiData.last_updated || pageData.lastUpdated,
            companyName: apiData.content?.companyName || pageData.companyName,
            contactEmail: apiData.content?.contactEmail || pageData.contactEmail,
            contactPhone: apiData.content?.contactPhone || pageData.contactPhone,
            sections: mappedSections
          });
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
      }
    };

    fetchPageData();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      Shield,
      Eye,
      Lock,
      Users,
      FileText,
      Calendar,
      Mail,
      Phone
    };
    return iconMap[iconName] || Shield; // Fallback to Shield
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 pt-20 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 dark:from-blue-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 dark:from-indigo-500/10 dark:to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-300/10 to-purple-300/10 dark:from-blue-400/5 dark:to-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Back to Home Link */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
        <Link
          to="/"
          className="inline-flex items-center px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl border border-white/20 dark:border-gray-700/50 hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block mb-8"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <Shield className="w-10 h-10 text-white" />
              </div>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-400 dark:via-purple-400 dark:to-indigo-400 bg-clip-text text-transparent mb-6">
              {pageData.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              {pageData.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-xl px-4 py-2">
                <Calendar className="w-4 h-4" />
                <span>Last Updated: {pageData.lastUpdated}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-xl px-4 py-2">
                <FileText className="w-4 h-4" />
                <span>{pageData.companyName}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-2xl rounded-3xl p-6 border border-white/20 dark:border-gray-700/50 sticky top-8">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Table of Contents</h2>
              <nav className="space-y-2">
                {pageData.sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-2xl text-sm transition-all duration-300 ${
                      activeSection === section.id
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg font-semibold'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="lg:col-span-3"
          >
            <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/20 dark:border-gray-700/50">
              {pageData.sections.map((section, index) => {
                const IconComponent = typeof section.icon === 'string' ? getIconComponent(section.icon) : (section.icon || Shield);
                
                return (
                  <motion.div
                    key={section.id}
                    id={section.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                    className="mb-12 last:mb-0"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {section.title}
                      </h2>
                    </div>
                    
                    <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                      {section.content}
                    </p>

                    {section.subsections && (
                      <div className="space-y-6">
                        {section.subsections.map((subsection, subIndex) => (
                          <motion.div
                            key={subIndex}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.8 + index * 0.1 + subIndex * 0.1, duration: 0.6 }}
                            className="card-3d bg-gray-50/80 dark:bg-gray-700/80 backdrop-blur-md rounded-2xl p-6 border border-gray-200/50 dark:border-gray-600/50"
                          >
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                              {subsection.title}
                            </h3>
                            <p className="text-gray-700 dark:text-gray-300">
                              {subsection.content}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                );
              })}

              {/* Contact Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="card-3d bg-gradient-to-r from-blue-50/80 to-purple-50/80 dark:from-blue-900/20 dark:to-purple-900/20 backdrop-blur-xl rounded-3xl p-8 border border-blue-200/50 dark:border-blue-700/50"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center gap-4 p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white">Email</p>
                      <a href={`mailto:${pageData.contactEmail}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                        {pageData.contactEmail}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white">Phone</p>
                      <a href={`tel:${pageData.contactPhone}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                        {pageData.contactPhone}
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 